import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CwfActiveDocumentByCarePlanError,
  CwfActiveDocumentByCarePlanRequest,
  CwfActiveDocumentByCarePlanResponse,
  CwfActiveDocumentByTypeError,
  CwfActiveDocumentByTypeRequest,
  CwfActiveDocumentByTypeResponse,
  CwfActiveDocumentsError,
  CwfActiveDocumentsRequest,
  CwfActiveDocumentsResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';

const KEY = {
  GET_ACTIVE_DOCUMENTS: 'GET_ACTIVE_DOCUMENTS',
  GET_DOCUMENT_BY_CARE_PLAN: 'GET_DOCUMENT_BY_CARE_PLAN',
  GET_DOCUMENT_BY_TYPE: 'GET_DOCUMENT_BY_TYPE',
} as const;

const request = {
  getActiveDocumentByCarePlan: async (
    {
      payload: { care_activity_id: careActivityId, care_plan_id: carePlanId, type_id: typeId },
    }: CwfActiveDocumentByCarePlanRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<CwfActiveDocumentByCarePlanResponse>(
      `${ENDPOINTS.FCT.CWF.DOCUMENTS.CARE_PLANS}/${carePlanId}/activities/${careActivityId}`,
      {
        params: { type_id: typeId },
        signal,
      },
    );

    return data;
  },
  getActiveDocumentByType: async (
    { payload: { study_id: studyId, type_id: typeId } }: CwfActiveDocumentByTypeRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<CwfActiveDocumentByTypeResponse>(
      `${ENDPOINTS.FCT.CWF.DOCUMENTS.STUDY}/${studyId}/type/${typeId}`,
      {
        signal,
      },
    );

    return data;
  },
  getActiveDocuments: async (
    { payload: { params } }: CwfActiveDocumentsRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<CwfActiveDocumentsResponse>(
      ENDPOINTS.FCT.CWF.DOCUMENTS.ACTIVE,
      {
        params: removeEmptyParams(params),
        signal,
      },
    );

    return data;
  },
};

const query = {
  useGetActiveDocumentByCarePlan: (
    { payload }: CwfActiveDocumentByCarePlanRequest,
    config?: Partial<
      UseQueryOptions<CwfActiveDocumentByCarePlanResponse, CwfActiveDocumentByCarePlanError>
    >,
  ) =>
    useQuery<CwfActiveDocumentByCarePlanResponse, CwfActiveDocumentByCarePlanError>({
      queryFn: ({ signal }) => request.getActiveDocumentByCarePlan({ payload }, signal),
      queryKey: [
        KEY.GET_DOCUMENT_BY_CARE_PLAN,
        payload.care_plan_id,
        payload.care_activity_id,
        payload.type_id,
      ],
      ...config,
    }),
  useGetActiveDocumentByType: (
    { payload }: CwfActiveDocumentByTypeRequest,
    config?: Partial<
      UseQueryOptions<CwfActiveDocumentByTypeResponse, CwfActiveDocumentByTypeError>
    >,
  ) =>
    useQuery<CwfActiveDocumentByTypeResponse, CwfActiveDocumentByTypeError>({
      queryFn: ({ signal }) => request.getActiveDocumentByType({ payload }, signal),
      queryKey: [KEY.GET_DOCUMENT_BY_TYPE, payload.study_id, payload.type_id],
      ...config,
    }),
  useGetActiveDocuments: (
    { payload }: CwfActiveDocumentsRequest,
    config?: Partial<UseQueryOptions<CwfActiveDocumentsResponse, CwfActiveDocumentsError>>,
  ) =>
    useQuery<CwfActiveDocumentsResponse, CwfActiveDocumentsError>({
      queryFn: ({ signal }) => request.getActiveDocuments({ payload }, signal),
      queryKey: [KEY.GET_ACTIVE_DOCUMENTS, payload.params],
      ...config,
    }),
};

export const documents = {
  KEY,
  query,
  request,
};
